import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check, Building2, ArrowRight } from 'lucide-react';

// Launch plan structure (locked). Prices MUST match app/api/signup.py PLANS price_cents.
// Solo $79 (price_cents 7900), Premier $199 (price_cents 19900).
// Seat add-ons are NOT sold at launch — UI shows included seats only; more = contact David.
const PLANS = [
  {
    key: 'solo',
    name: 'Solo',
    price: '$79',
    period: '/mo',
    seats: '2 seats included',
    tagline: 'Run a solo practice end to end.',
    features: [
      'Case management, contacts & calendar',
      'Documents, time tracking & billing',
      'Trust accounting',
      'Client portal',
      'Mail integration (your existing email)',
    ],
  },
  {
    key: 'premier',
    name: 'Premier',
    price: '$199',
    period: '/mo',
    seats: '10 seats included',
    tagline: 'For firms with staff and AI in the workflow.',
    featured: true,
    features: [
      'Everything in Solo',
      'AI: mail, documents, cases & sigil assistant',
      'AI receptionist (Clarissa)',
      'OuijaMail hosted mail (custom domain or subdomain)',
      'Multi-staff with role management',
      'Priority support + onboarding',
    ],
  },
];

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firm_name: '', name: '', email: '', password: '', confirmPassword: '',
    plan: 'solo', custom_mail_domain: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);

  // Idempotency token (Square CATCH-1): mint ONCE on mount, never on re-render.
  // StrictMode double-invokes effects in dev — the `if (!current)` guard keeps it stable.
  // Resent on accidental resubmit (back/double-click) so Square dedupes; a deliberate
  // fresh page load remounts -> new token -> new subscription. crypto.randomUUID is
  // native (HTTPS/secure context); a fallback covers non-secure dev contexts.
  const idempotencyToken = useRef(null);
  useEffect(() => {
    if (!idempotencyToken.current) {
      idempotencyToken.current =
        (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : `fe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }, []);

  // Initialize Square Web Payments SDK
  useEffect(() => {
    const appId = process.env.REACT_APP_SQUARE_APP_ID;
    const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;
    if (!appId || !locationId) {
      console.warn('Square app ID or location ID not configured');
      return;
    }

    const initSquare = async () => {
      if (!window.Square) {
        console.error('Square SDK not loaded');
        return;
      }
      try {
        const payments = window.Square.payments(appId, locationId);
        paymentsRef.current = payments;
        const card = await payments.card();
        await card.attach('#card-container');
        cardRef.current = card;
        setCardReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        setError('Payment form failed to load. Please refresh.');
      }
    };

    // Load Square SDK script if not already present
    if (!window.Square) {
      const script = document.createElement('script');
      script.src = 'https://web.squarecdn.com/v1/square.js';
      script.onload = initSquare;
      document.head.appendChild(script);
    } else {
      initSquare();
    }

    return () => {
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch {}
      }
    };
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!cardRef.current) {
      setError('Payment form not ready. Please wait or refresh.');
      return;
    }

    setLoading(true);

    try {
      // Tokenize card
      const tokenResult = await cardRef.current.tokenize();
      if (tokenResult.status !== 'OK') {
        setError(tokenResult.errors?.[0]?.message || 'Card validation failed');
        setLoading(false);
        return;
      }

      // custom_mail_domain only applies to Premier; send null otherwise / when blank.
      const domain = form.custom_mail_domain.trim();
      const customMailDomain = form.plan === 'premier' && domain ? domain : null;

      // Submit signup
      const res = await fetch(`${API_BASE}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firm_name: form.firm_name,
          name: form.name,
          email: form.email,
          password: form.password,
          plan: form.plan,
          source_id: tokenResult.token,
          idempotency_token: idempotencyToken.current,
          custom_mail_domain: customMailDomain,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Signup failed. Please try again.');
        setLoading(false);
        return;
      }

      // Store token and redirect to dashboard
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('tenant_id', data.tenant_id);
      navigate('/');
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find((p) => p.key === form.plan);

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0f1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, sans-serif", padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: 480, background: '#0f1929',
        border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
        padding: '40px 36px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '0.18em', color: '#f5a623' }}>
            VIRGO
          </div>
          <div style={{ fontSize: 13, color: '#7a8fa6', marginTop: 4 }}>
            Start your practice in minutes
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#f87171', borderRadius: 8, padding: '10px 14px',
            fontSize: 13, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Firm Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
              Firm Name
            </label>
            <div style={{ position: 'relative' }}>
              <Building2 size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#4a5f75' }} />
              <input
                name="firm_name" value={form.firm_name} onChange={handleChange} required
                placeholder="Smith Law PLLC"
                style={{
                  width: '100%', padding: '10px 12px 10px 36px', background: '#1a2332',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
              Your Name
            </label>
            <input
              name="name" value={form.name} onChange={handleChange} required
              placeholder="John Smith"
              style={{
                width: '100%', padding: '10px 12px', background: '#1a2332',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
              Email
            </label>
            <input
              name="email" type="email" value={form.email} onChange={handleChange} required
              placeholder="john@smithlaw.com"
              style={{
                width: '100%', padding: '10px 12px', background: '#1a2332',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Password */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  name="password" type={showPassword ? 'text' : 'password'}
                  value={form.password} onChange={handleChange} required
                  placeholder="Min 8 characters"
                  style={{
                    width: '100%', padding: '10px 36px 10px 12px', background: '#1a2332',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                    color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 8, top: 8, background: 'none',
                    border: 'none', color: '#4a5f75', cursor: 'pointer', padding: 4,
                  }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
                Confirm
              </label>
              <input
                name="confirmPassword" type="password"
                value={form.confirmPassword} onChange={handleChange} required
                placeholder="Confirm password"
                style={{
                  width: '100%', padding: '10px 12px', background: '#1a2332',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Plan Selection */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 8 }}>
              Plan
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {PLANS.map((plan) => (
                <button
                  key={plan.key} type="button"
                  onClick={() => setForm({ ...form, plan: plan.key })}
                  style={{
                    position: 'relative',
                    padding: '14px 12px', borderRadius: 8, cursor: 'pointer',
                    background: form.plan === plan.key ? 'rgba(13,159,143,0.15)' : '#1a2332',
                    border: form.plan === plan.key
                      ? '2px solid #0d9f8f'
                      : plan.featured ? '1px solid rgba(245,166,35,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center', transition: 'all 0.15s',
                  }}>
                  {plan.featured && (
                    <div style={{
                      position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)',
                      background: '#f5a623', color: '#0a0f1a', fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
                    }}>
                      MOST POPULAR
                    </div>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#e8edf5' }}>
                    {plan.name}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#0d9f8f', margin: '4px 0' }}>
                    {plan.price}<span style={{ fontSize: 12, fontWeight: 400, color: '#7a8fa6' }}>{plan.period}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#7a8fa6' }}>{plan.seats}</div>
                  {form.plan === plan.key && (
                    <Check size={16} style={{ color: '#0d9f8f', marginTop: 4 }} />
                  )}
                </button>
              ))}
            </div>

            {/* Selected plan: what's included */}
            <div style={{
              marginTop: 12, padding: '12px 14px', background: '#141d2b',
              border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8,
            }}>
              <div style={{ fontSize: 12, color: '#a0b4c8', marginBottom: 8 }}>
                {selectedPlan?.tagline}
              </div>
              {selectedPlan?.features.map((f) => (
                <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
                  <Check size={14} style={{ color: '#0d9f8f', flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 12, color: '#c8d4e2' }}>{f}</span>
                </div>
              ))}
              <div style={{ fontSize: 10, color: '#4a5f75', marginTop: 8 }}>
                Need more than {selectedPlan?.key === 'premier' ? '10' : '2'} seats? Contact us after signup.
              </div>
            </div>
          </div>

          {/* Custom mail domain — Premier only, optional */}
          {form.plan === 'premier' && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 6 }}>
                Custom mail domain (optional)
              </label>
              <input
                name="custom_mail_domain" value={form.custom_mail_domain} onChange={handleChange}
                placeholder="mail.smithlaw.com"
                style={{
                  width: '100%', padding: '10px 12px', background: '#1a2332',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: '#e8edf5', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 11, color: '#4a5f75', marginTop: 6 }}>
                Leave blank to use a free Virgo subdomain (works immediately). A custom domain
                requires a quick DNS setup after signup.
              </div>
            </div>
          )}

          {/* Square Card Form */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#7a8fa6', marginBottom: 8 }}>
              Payment
            </label>
            <div
              id="card-container"
              style={{
                background: '#1a2332', borderRadius: 8, padding: '12px',
                border: '1px solid rgba(255,255,255,0.1)', minHeight: 50,
              }}
            />
            <div style={{ fontSize: 11, color: '#4a5f75', marginTop: 6 }}>
              You will be charged {selectedPlan?.price}/month starting today. Cancel anytime.
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !cardReady}
            style={{
              width: '100%', padding: '14px', borderRadius: 8, border: 'none',
              background: loading || !cardReady ? '#1a3a3a' : '#0d9f8f',
              color: '#fff', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: loading || !cardReady ? 0.6 : 1,
              transition: 'all 0.15s',
            }}>
            {loading ? 'Creating your account...' : <>Start Your Practice <ArrowRight size={18} /></>}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#4a5f75' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#0d9f8f', textDecoration: 'none' }}>Log in</Link>
        </div>
      </div>
    </div>
  );
}
